import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "components/lib/Shared/Icons/sflIcons";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { usePopper } from "hooks/usePopper";
import { useToast } from "hooks/useToast";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import ReactTooltip from "react-tooltip";
import { updateCompanyAsync } from "state/slices/company";
import { transactionReviewAsync } from "state/slices/initiateTransactionChange";
import { getRegisterOfApplicationsAndAllotmentsAsync } from "state/slices/minuteBook";
import { updateTransaction } from "state/slices/transaction";
import {
  directorApprovals,
  shareholerApprovals,
} from "utils/companyGovernanceSummaryReferral";
import {
  checkUnsignedChangesDocsArePresent,
  checkUnsignedDirectorApprovalDocsArePresent,
  checkUnsignedShareholderApprovalDocsArePresent,
} from "utils/unsignedTransactionDocs";
import ApplicantDetails from "./ApplicantDetails";
import Approvals from "./Approvals";
import BeneficialOwnership from "./BeneficialOwnership";
import Uploads from "./BRSFormsUpload";
import StakeholderDetails from "./CompanyOfficials/StakeholderDetails";
import ContactPerson from "./ContactPerson";
import ESignature from "./ESignature";
import ESignatureDocsPreparation from "./ESignatureDocsPreparation";
import ManualBridge from "./ManualBridge";
import RegisteredAddress from "./RegisteredAddress.jsx";
import {
  getNextStepLabel,
  getNextTransactionStatusAndToastMessage,
} from "./reviewSteps";
import ShareInformation from "./ShareInformation";
import Status from "./Status";
import Summary from "./Summary";
import TypeOfChange from "./TypeOfChange";

// Helper functions
const checkOfficialHasUnsignedDocs = (official) => {
  return official?.uploadChangeDocument?.modeType === "not-signed-documents";
};

const checkOfficialsHasEnvelopes = (officials) => {
  return officials?.some((official) => {
    return official?.envelopes?.length > 0;
  });
};

const checkReviewIsInDirectorsStep = (transactions) => {
  return transactions.some((transaction) => {
    return (
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_APPROVAL ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW_II
    );
  });
};

const checkReviewIsInAdminStep = (transactions) => {
  return transactions.some((transaction) => {
    return (
      /** OrgAdmin level */
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_REVIEW_APPROVAL ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II ||
      /** BRS / Super Admin level */
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II
    );
  });
};

const showManualBridge = (transactions) => {
  return transactions.some((transaction) => {
    return (
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_II ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II
    );
  });
};

const checkIsInSubmission = (transactions) => {
  return transactions.some((transaction) => {
    return (
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION ||
      transaction.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_II
    );
  });
};

const showBrsForms = (transactions) => {
  return (
    transactions?.some(
      (trx) =>
        (trx?.fileUploads && Object.keys(trx?.fileUploads).length > 0) ||
        (trx?.presignedDocuments &&
          Object.keys(trx?.presignedDocuments).length > 0)
    ) || checkIsInSubmission(transactions)
  );
};

/**
 * TransactionReviewTab component
 * @param {*} props
 * @returns
 */

// ================= Variable Declarations =================
const TransactionReviewTab = (props) => {
  const {
    historyTransaction,
    isLastPeriod,
    isFirstPeriod,
    isLastTransactionInPeriod,
    isFirstTransactionInPeriod,
    goHistoryNextStep,
    goHistoryPreviousStep,
  } = props;
  const { transactionUnderReview, getTransactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const companySlice = useSelector((state) => state.companySlice);

  const {
    manageCompanyTransactions,
    dashboardPermissions,
    companyOfficials,
    dashboardRoles,
    designation,
  } = useSelector((state) => state.companyDashboardSlice);

  /** Check if the logged in user is Reviewer or Approver at any stage */
  const { dashboardRoles: adminRoles, dashboardPermissions: adminPermissions } =
    useSelector((state) => state.activeDashboardSlice);

  // Org Admin Roles
  const isOrgAdminReviewer = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.REVIEWER
  );
  const isOrgAdminApprover = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.APPROVER ||
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );
  const isOrgAdminUploader = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.UPLOADER
  );

  // Super Admin Roles
  const isSuperAdminReviewer = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.REVIEWER
  );
  const isSuperAdminApprover = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.APPROVER ||
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );
  const isSuperAdminUploader = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.UPLOADER
  );

  //Brs Ops Roles
  const isBrsOpsReviewer = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.REVIEWER
  );
  const isBrsOpsApprover = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.APPROVER ||
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );
  const isBrsOpsUploader = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.UPLOADER
  );

  let transactions = [];
  if (historyTransaction) {
    transactions = transactionUnderReview.items.transactions.filter((trx) => {
      if (historyTransaction?.batchNo && trx?.batchNo) {
        return trx.batchNo === historyTransaction.batchNo;
      }
      return trx.trxRef === historyTransaction.trxRef;
    });
  } else {
    transactions = transactionUnderReview.items.transactions;
  }

  const { directorsWhoHaveApproved, shareholdersWhoHaveApproved } =
    transactionUnderReview.items;
  const [agreeToSubmit, setAgreeToSubmit] = useState(
    transactions[0]?.status !== Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
  );
  const [agreeToTerms, setAgreeToTerms] = useState(
    transactions[0]?.status !== Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT
  );
  const [submitting, setSubmitting] = useState(false);
  const isViewMode = props?.isViewMode || false;
  const [selectedFiles, setSelectedFiles] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(
    getTransactionUnderReview.status
  );
  const isInManualTransactionReviewContext = transactions.some(
    (trx) => trx?.transactionType === Constants.TRANSACTION_TYPES.MANUAL
  );
  const [company, setCompany] = useState({});
  const [
    directorsMeetThresholdAsPerGovernanceSummary,
    setDirectorsMeetThresholdAsPerGovernanceSummary,
  ] = useState(false);
  const [
    shareholdersMeetThresholdAsPerGovernanceSummary,
    setShareholdersMeetThresholdAsPerGovernanceSummary,
  ] = useState(false);
  const [currentUserHasApproved, setCurrentUserHasApproved] = useState(false);
  const params = useParams();
  const [tabs, setTabs] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState({});
  const currentUser = useSelector((state) => state.userSlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const { getEmployee } = useSelector((state) => state.employeeSlice);
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const history = useHistory();
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true);
  const [legalDepartmentEmployees, setLegalDepartmentEmployees] = useState([]);
  const [nonLegalDepartmentEmployees, setNonLegalDepartmentEmployees] =
    useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [esignatureIsSelected, setEsignatureIsSelected] = useState(false);

  const [unsignedDocsArePresent, setUnsignedDocsArePresent] = useState(false);
  const [eSignatureIsForDirectors, setESignatureIsForDirectors] =
    useState(false);
  const [eSignatureIsForShareholders, setESignatureIsForShareholders] =
    useState(false);
  const [eSignatureIsForUploadChangeDocs, setESignatureIsForUploadChangeDocs] =
    useState(false);
  const [eSignatureIsForAdmin, setESignatureIsForAdmin] = useState(false);

  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  /** Use effects */

  useEffect(() => {
    if (transactionUnderReview?.items?.transactions?.length > 0) {
      // Check if unsigned documents are present in the transaction under review
      const hasUnsignedDocs = transactions.find((trx) =>
        checkUnsignedChangesDocsArePresent(trx)
      );
      const hasDirectorUnsignedDocs = transactions.find((trx) =>
        checkUnsignedDirectorApprovalDocsArePresent(trx)
      );
      const hasShareholderUnsignedDocs = transactions.find((trx) =>
        checkUnsignedShareholderApprovalDocsArePresent(trx)
      );

      const reviewIsInAdminStep = checkReviewIsInAdminStep(
        transactionUnderReview?.items?.transactions
      );

      // Set the state based on the presence of unsigned documents and the current review step
      if (
        hasUnsignedDocs ||
        (isLegalDepartmentReviewOrApprove && hasUnsignedDocs)
      ) {
        setESignatureIsForUploadChangeDocs(true);
        setUnsignedDocsArePresent(true);
      }
      if (hasDirectorUnsignedDocs && !hasUnsignedDocs) {
        setESignatureIsForDirectors(true);
        setUnsignedDocsArePresent(true);
      }
      if (hasShareholderUnsignedDocs && !hasDirectorUnsignedDocs) {
        setESignatureIsForShareholders(true);
        setUnsignedDocsArePresent(true);
      }
    }
  }, [transactionUnderReview]);

  useEffect(() => {
    setLoadingStatus(getTransactionUnderReview.status);
  }, [getTransactionUnderReview.status]);

  useEffect(() => {
    if (companySlice.company) {
      setCompany(companySlice.company);
    }
  }, [companySlice.company]);

  useEffect(() => {
    const checkUserApproval = (approvers, userEmail) =>
      approvers?.find(({ email }) => email === userEmail) || null;

    const updateDirectorApprovals = () => {
      //if (!directorsWhoHaveApproved) return;

      setCurrentUserHasApproved(
        checkUserApproval(directorsWhoHaveApproved, currentUser.email)
      );

      const totalDirectorsApproved = directorsWhoHaveApproved?.length;
      setDirectorsMeetThresholdAsPerGovernanceSummary(
        directorApprovals(
          totalDirectorsApproved,
          company,
          companyOfficials.items
        )
      );
    };

    const updateShareholderApprovals = () => {
      //if (!shareholdersWhoHaveApproved) return;

      setCurrentUserHasApproved(
        checkUserApproval(shareholdersWhoHaveApproved, currentUser.email)
      );

      setShareholdersMeetThresholdAsPerGovernanceSummary(
        shareholerApprovals(
          shareholdersWhoHaveApproved,
          company,
          companyOfficials.items
        )
      );
    };

    updateDirectorApprovals();
    updateShareholderApprovals();
  }, [
    directorsWhoHaveApproved,
    shareholdersWhoHaveApproved,
    company,
    currentUser.email,
  ]);

  const getTransactionsApplicationTypes = () => {
    return transactions.map((trx) => trx?.applicationType);
  };

  const getTransactionsChangeTypes = () => {
    return transactions.map((trx) => trx?.changeType);
  };

  const noApprovalsTransactionTypes = [
    Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION,
    Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS,
    Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_DISCHARGE,
  ];

  const showContactPerson = () => {
    return transactions.find(
      (trx) =>
        trx.applicationType ===
        Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY &&
        trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT
    );
  };

  const buildTabs = () => {
    let tempTabs = [];
    tempTabs.push(
      {
        id: "applicantDetails",
        label: "Applicant Details",
      },
      {
        id: "typeOfChange",
        label: "Type Of Change",
      }
    );
    if (
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
      )
    ) {
      tempTabs.push({
        id: "summary",
        label: "Summary",
      });
    }
    if (
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
      )
    ) {
      tempTabs.push({
        id: "registeredAddress",
        label: "Registered Address",
      });
    }
    if (
      getTransactionsApplicationTypes().includes(
        Constants.TRANSACTION_APPLICATION_TYPES.SHARE_CAPITAL
      )
    ) {
      tempTabs.push({
        id: "shareInformation",
        label: "Share Information",
      });
    }
    if (getTransactionsChangeTypes().includes(
      Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT
    ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CESSATION
      ) ||
      getTransactionsChangeTypes().includes(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS
      )) {
      tempTabs.push({
        id: "stakeholderDetails",
        label: "Stakeholder Details",
      });
    }
    if (
      transactionUnderReview?.items?.beneficialOwners?.length > 0 ||
      getTransactionsApplicationTypes().includes(
        Constants.TRANSACTION_APPLICATION_TYPES.SHARE_CAPITAL
      )
    ) {
      tempTabs.push({
        id: "beneficialOwners",
        label: "Beneficial Owners",
      });
    }

    if (showContactPerson()) {
      tempTabs.push({
        id: "contactPerson",
        label: "Contact Person",
      });
    }

    if (transactions?.length === 1) {
      if (!noApprovalsTransactionTypes.includes(transactions[0]?.changeType)) {
        tempTabs.push({
          id: "approvals",
          label: "Approvals",
        });
      }

      if (
        transactions[0]?.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS &&
        !transactions[0]?.applicationType ===
        Constants.APPLICANT_CAPACITIES.ADMINISTRATOR
      ) {
        tempTabs.push({
          id: "approvals",
          label: "Approvals",
        });
      }
    }

    if (
      transactions?.length > 1 &&
      !noApprovalsTransactionTypes.every((item) =>
        transactions.map((trx) => trx.changeType).includes(item)
      )
    ) {
      tempTabs.push({
        id: "approvals",
        label: "Approvals",
      });
    }

    if (
      transactionUnderReview?.items?.transactions?.length > 0 &&
      (transactionUnderReview?.items?.transactions[0]?.envelopes?.length > 0 ||
        checkOfficialsHasEnvelopes(transactionUnderReview?.items?.officials))
    ) {
      tempTabs.push({
        id: "eSignature",
        label: "E-Signature",
      });
    }

    if (showManualBridge(transactionUnderReview.items.transactions)) {
      tempTabs.push({
        id: "manualBridge",
        label: "Manual Bridge",
      });
    }

    if (showBrsForms(transactionUnderReview.items.transactions)) {
      tempTabs.push({
        id: "brsForms",
        label: "BRS Forms",
      });
    }

    tempTabs.push({
      id: "status",
      label: "Status",
    });

    tempTabs = tempTabs.filter(Boolean);
    return tempTabs;
  };

  useEffect(() => {
    if (transactions.length > 0) {
      setTabs(buildTabs());
    }
  }, [transactionUnderReview.items, historyTransaction]);

  useEffect(() => {
    if (tabs.length > 0) {
      setSelectedTabIndex(0);
      setSelectedTab(tabs[0]);
    }
  }, [tabs.length, historyTransaction]);

  // ================= Computed Variables =================

  const isDirectorReviewOrApprove = dashboardRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER &&
      [
        Constants.APPLICANT_CAPACITIES.DIRECTOR,
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
      ].includes(designation)
  );

  const isRegistrarReviewOrApprove = dashboardRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );

  const isLegalDepartmentReviewOrApprove = legalDepartmentEmployees.some(
    (emp) => emp.user._id === currentUser._id
  );
  const isReviewer = (() => {
    if (transactions?.length > 0) {
      if (
        isLegalDepartmentReviewOrApprove &&
        [Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW].includes(
          transactions[0].status
        )
      ) {
        return true;
      }
      if (
        isDirectorReviewOrApprove &&
        [
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL,
        ].includes(transactions[0].status)
      ) {
        return true;
      }
      if (isRegistrarReviewOrApprove) {
        return true;
      }
      return false;
    }
  })();

  const isApprover = (() => {
    if (transactions?.length > 0) {
      if (
        isLegalDepartmentReviewOrApprove &&
        [
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_REVIEW,
        ].includes(transactions[0].status)
      ) {
        return true;
      }
      if (
        isDirectorReviewOrApprove &&
        [
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_REVIEW,
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_REVIEW,
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_APPROVAL,
        ].includes(transactions[0].status)
      ) {
        return true;
      }
      if (isRegistrarReviewOrApprove) {
        return true;
      }

      return false;
    }
  })();


  let unresolvedComments = comments.filter((comment) => !comment.isResolved);
  const submitToLabel =
    unresolvedComments.length !== 0
      ? "to the applicant"
      : isInManualTransactionReviewContext
        ? "for processing. "
        : "" +
        getNextStepLabel({
          status: transactions[0].status,
          isApprover,
          isOrgAdminApprover,
          isSuperAdminApprover,
          isBrsOpsApprover,
        });
  const isFirstTabIndex = selectedTabIndex === 0;
  const isLastTabIndex = selectedTabIndex === tabs.length - 1;
  const isLastHistoryStep = isLastTransactionInPeriod && isLastPeriod
  const isFirstHistoryStep = isFirstTransactionInPeriod && isFirstPeriod

  let isFirstStep = false
  let isLastStep = false
  if (historyTransaction) {
    isFirstStep = isFirstHistoryStep && isFirstTabIndex
    isLastStep = isLastHistoryStep && isLastTabIndex
  } else {
    isFirstStep = isFirstTabIndex
    isLastStep = isLastTabIndex
  }


  let nextBtnLabel = isLastStep
    ? isViewMode
      ? "Back to Transactions Page"
      : `Submit to ${submitToLabel}`
    : "Next Step"

  if (historyTransaction) {
    if (isLastTabIndex && isLastTransactionInPeriod) {
      nextBtnLabel = "Next ARD Period"
    } else {
      nextBtnLabel = "Save and Proceed"
    }
  }


  // ================= Handlers =================
  // Handles navigation to the previous tab/step
  const goToPrevStep = () => {
    if (historyTransaction) {
      if (isFirstTabIndex && !isFirstHistoryStep) {
        goHistoryPreviousStep()
      } else {
        setSelectedTabIndex(selectedTabIndex - 1);
      }
    } else {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  }

  // Handles navigation to the next tab/step
  const goToNextStep = () => {
    if (historyTransaction) {
      if (isLastTabIndex && !isLastHistoryStep) {
        goHistoryNextStep()
      } else {
        setSelectedTabIndex(selectedTabIndex + 1);
      }
    } else {
      setSelectedTabIndex(selectedTabIndex + 1);
    }

  };

  /**
   * Handles the main application submission logic for the transaction review process.
   * Iterates through transactions that require review in this step, determines the next status,
   * dispatches the async review action, and shows appropriate toasts.
   * Handles robust error logging for each transaction and the overall process.
   */
  const handleSubmitApplication = async () => {
    let transactionsToProcess = []
    if (historyTransaction) {
      transactionsToProcess = [historyTransaction];
    } else {
      transactionsToProcess = transactions;
    }
    try {
      for (const transaction of transactionsToProcess) {
        try {
          const { status, toastMessage } =
            getNextTransactionStatusAndToastMessage({
              transaction,
              unresolvedComments,
              legalDepartmentIsPresent: legalDepartmentEmployees?.length > 0,
              isApprover,
              isOrgAdminApprover,
              isSuperAdminApprover,
              isBrsOpsApprover,
            });



          if (!status) {
            console.error(`Status is undefined for transaction:`, transaction);
            continue;
          }

          if (
            transaction.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
            status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED
          ) {
            // Upadte the company status from strike of ongoing to original active status before we process the transaction and create the version
            const companyRes = await dispatch(
              updateCompanyAsync({
                id: params.companyId,
                data: {
                  status: Constants.ENTITY_STATUS.ACTIVE,
                },
              })
            );
            if (companyRes?.error) {
              toast("error", `Failed to update the transaction.`);
              return;
            } else {
              toast(
                "success",
                `The company status updated to Strike Off Ongoing`
              );
            }
          }

          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION &&
            status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED
          ) {
            await dispatch(
              updateTransaction({
                trxId: transaction._id,
                data: {
                  status,
                  companyChangedFields: {
                    ...transaction.companyChangedFields,
                    status: Constants.ENTITY_STATUS.ACTIVE,
                  },
                },
              })
            );
          } else {
            const trxRes = await dispatch(
              updateTransaction({
                trxId: transaction._id,
                data: {
                  status,
                },
              })
            );

            if (trxRes?.payload?.status) {
              toast("success", toastMessage);
            }

          }
          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES
              .NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF
          ) {
            const strikeOffTransactionTocancel =
              initiatedTransactions?.items?.find(
                (item) =>
                  item?.status &&
                  item?.status !==
                  Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.PROCESSED &&
                  item?.status !==
                  Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT &&
                  item?.status !==
                  Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CANCELLED &&
                  item?.changeType ===
                  Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
              );

            const companyRes = await dispatch(
              updateCompanyAsync({
                id: params.companyId,
                data: {
                  status: Constants.ENTITY_STATUS.ACTIVE,
                },
              })
            );
            if (companyRes?.error) {
              toast("error", `Failed to update the transaction.`);
              return;
            } else {
              toast(
                "success",
                `The company status updated to Strike Off Ongoing`
              );
            }

            await dispatch(
              updateTransaction({
                trxId: strikeOffTransactionTocancel._id,
                data: {
                  status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CANCELLED,
                },
              })
            );
          }

          if (
            transaction?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION &&
            transaction?.fileUploads?.courtOrder
          ) {
            const transationToBeCancelled =
              transaction?.companyChangedFields?.transactionToCancelDetails;
            if (transaction?.companyChangedFields?.upholdConversionResolution) {
              const previousStatus = transationToBeCancelled?.status;
              await dispatch(
                updateTransaction({
                  trxId: transationToBeCancelled._id,
                  data: {
                    status: previousStatus,
                  },
                })
              );
            }
            if (transaction?.companyChangedFields?.cancelConversionResolution) {
              await dispatch(
                updateTransaction({
                  trxId: transationToBeCancelled._id,
                  data: {
                    status:
                      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CANCELLED,
                  },
                })
              );
            }
          }

          // Dispatch the async review action
          await dispatch(
            transactionReviewAsync({
              companyId: transaction.companyId,
              data: {
                trxRef: transaction.trxRef,
                status,
                infoForEmailToBeSent: {
                  name: transaction.names?.firstName ?? "N/A",
                  email: transaction.electronicAddress?.emailAddress ?? "N/A",
                  reviewer: currentUser.names?.firstName ?? "N/A",
                  companyName:
                    transaction.company?.companyName1WithSuffix ?? "N/A",
                  unresolvedCommentsCount: unresolvedComments?.length || 0,
                },
              },
            })
          );


        } catch (err) {
          // Log errors for individual transaction processing
          console.error(
            "Error processing transaction:",
            transaction.status,
            err
          );
        }
      }
    } catch (error) {
      // Log errors for the overall submission process
      console.error("Error in handleSubmitApplication:", error);
    }

    // Redirect to transactions dashboard after submission
    if (params.companyId) {
      history.push(`/companies/${params.companyId}/dashboard?transactions`);
    }

    // Refresh table data after submission for orgAdmin and Super Admin
    if (props?.handleBackBtn) {
      props.handleBackBtn();
    }
  };

  const handleConfirmDetails = async () => {
    setSubmitting(true);
    try {
      await handleSubmitApplication();
    } catch (error) {
      console.error("Error in handleConfirmDetails:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // ================= Effects for Tabs and Employees =================
  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex]);

  useEffect(() => {
    const companyId = transactions[0]?.companyId?._id || transactions[0]?.companyId;
    if (companyId) {
      dispatch(
        getRegisterOfApplicationsAndAllotmentsAsync({
          queryParams: { companyId },
        })
      );
    }
  }, [transactions, dispatch]);

  useEffect(() => {
    if (getEmployee?.items) {
      setLegalDepartmentEmployees(
        getEmployee.items.filter(
          (employee) => employee.isLegalDepartment === true
        )
      );
      setNonLegalDepartmentEmployees(
        getEmployee.items.filter(
          (employee) => employee.isLegalDepartment === false
        )
      );
    }
  }, [getEmployee]);

  /** handle e-signature and manual signing */

  const handleESignature = () => {
    setIsSubmitting(true)
    setEsignatureIsSelected(true);
    setIsSubmitting(false)
  };

  const handleManualSigning = () => {
    setIsSubmitting(true)
    handleConfirmDetails();
    setIsSubmitting(false)
  };

  const disableESignature = () => {
    setEsignatureIsSelected(false);
  };

  /** Handle legal department sending to the Registrar for signature setup */
  const handleSubmitToRegistrar = () => {
    transactions = transactions.map((transaction) => ({
      ...transaction,
      status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.LEGAL_SIGNATURE_SETUP,
    }));
    handleSubmitApplication();
  };

  const handleTransactionDeferred = () => {
    transactions = transactions.map((transaction) => ({
      ...transaction,
      status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DEFERRED,
    }));
    handleSubmitApplication();
  };

  const handleTransactionCancelled = () => {
    transactions = transactions.map((transaction) => ({
      ...transaction,
      status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CANCELLED,
    }));
    handleSubmitApplication();
  };

  // ================= Loading and Rendering =================
  if (loadingStatus === "loading") {
    return (
      <div>
        <SpinnerIcon />
      </div>
    );
  } else if (
    loadingStatus === "succeeded" &&
    (isViewMode === true ||
      (isViewMode === false && (isReviewer || isApprover)) ||
      props?.isFromAdmin)
  ) {
    if (esignatureIsSelected) {
      return (
        <ESignatureDocsPreparation
          setEsignatureIsSelected={setEsignatureIsSelected}
          handleConfirmDetails={handleConfirmDetails}
          eSignatureIsForDirectors={eSignatureIsForDirectors}
          eSignatureIsForShareholders={eSignatureIsForShareholders}
          eSignatureIsForUploadChangeDocs={eSignatureIsForUploadChangeDocs}
          eSignatureIsForAdmin={eSignatureIsForAdmin}
          goToPrevStep={disableESignature}
        />
      );
    } else {
      return (
        <div>
          <div>
            <Tabs
              tabs={tabs}
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={setSelectedTabIndex}
            />
          </div>
          <div className="mt-4">
            {selectedTab?.id && (
              <div>
                {selectedTab.id === "applicantDetails" && <ApplicantDetails />}
                {selectedTab.id === "typeOfChange" && (
                  <TypeOfChange
                    isViewMode={isViewMode}
                    transactions={transactions}
                  />
                )}
                {selectedTab.id === "summary" && (
                  <Summary isViewMode={isViewMode} transactions={transactions} />
                )}
                {selectedTab.id === "registeredAddress" && (
                  <RegisteredAddress isViewMode={isViewMode} transactions={transactions} />
                )}
                {selectedTab.id === "shareInformation" && (
                  <ShareInformation isViewMode={isViewMode} transactions={transactions} />
                )}
                {selectedTab.id === "stakeholderDetails" && (
                  <StakeholderDetails isViewMode={isViewMode} transactions={transactions} />
                )}
                {selectedTab.id === "beneficialOwners" && (
                  <BeneficialOwnership isViewMode={isViewMode} transactions={transactions} />
                )}
                {selectedTab.id === "contactPerson" && (
                  <ContactPerson
                    handleBackBtn={goToPrevStep}
                    handleNextBtn={goToNextStep}
                    transactions={transactions}
                  />
                )}
                {selectedTab.id === "approvals" && (
                  <Approvals
                    handleConfirmDetails={handleConfirmDetails}
                    goToNextParentTab={goToNextStep}
                    isViewMode={isViewMode}
                    goToPrevParentTab={goToPrevStep}
                    transactions={transactions}
                  />
                )}
                {selectedTab.id === "eSignature" && (
                  <ESignature isViewMode={isViewMode} />
                )}
                {selectedTab.id === "manualBridge" && <ManualBridge />}
                {selectedTab.id === "brsForms" && (
                  <Uploads
                    goToNextParentTab={goToNextStep}
                    isViewMode={isViewMode}
                    goToPrevStep={goToPrevStep}
                    handleConfirmDetails={handleConfirmDetails}
                    setESignatureIsSelected={setEsignatureIsSelected}
                    setESignatureIsForAdmin={setESignatureIsForAdmin}
                    handleBackBtn={props?.handleBackBtn}
                    defaultTransactions={transactions}
                  />
                )}
                {selectedTab.id === "status" && (
                  <Status
                    submitToLabel={submitToLabel}
                    setDisableSubmitBtn={setDisableSubmitBtn}
                    agreeToSubmit={agreeToSubmit}
                    agreeToTerms={agreeToTerms}
                    setAgreeToSubmit={setAgreeToSubmit}
                    setAgreeToTerms={setAgreeToTerms}
                    isViewMode={isViewMode}
                  />
                )}
                {!["brsForms", "approvals", "contactPerson"].includes(
                  selectedTab.id
                ) && (
                    <div className="flex justify-between">
                      {!isFirstStep && (
                        <button
                          onClick={goToPrevStep}
                          className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                        >
                          <span className="flex items-center mr-4 h-7">
                            <ChevronLeftIcon
                              className="flex-shrink-0 w-5 h-5"
                              aria-hidden="true"
                            />
                          </span>
                          <p className="font-semibold">Go back</p>
                        </button>
                      )}
                      {isLastStep && !isViewMode ? (
                        <div className="relative inline-block">
                          {unsignedDocsArePresent ? (
                            !agreeToSubmit || !agreeToTerms ? (
                              <button
                                className={`flex items-center px-4 py-2 mb-2 font-semibold mt-7
                           ${!agreeToSubmit || !agreeToTerms
                                    ? "cursor-not-allowed text-white rounded bg-green-focused/20"
                                    : "hover:bg-green-600 hover:shadow-lg text-white rounded bg-sflPrimary"
                                  } // Hover only when enabled
                         `}
                              >
                                eSignature
                              </button>
                            ) : (
                              <div className={`flex flex-row gap-0.5`}>
                                <button
                                  type="submit"
                                  onClick={handleESignature}
                                  className={`flex px-2 py-2 button button-hover button-disabled button-outline button-focus
                                     hover:bg-green-600 hover:shadow-lg text-white bg-sflPrimary rounded-l-md rounded-r-none
                                                  `}
                                >
                                  {esignatureIsSelected ? (
                                    isSubmitting ? (
                                      <>
                                        <SpinnerIcon />
                                        Loading E Signing...
                                      </>
                                    ) : (
                                      <>
                                        <div className="ml-1.5 flex items-center">
                                          E Signature
                                        </div>
                                      </>
                                    )
                                  ) : isSubmitting ? (
                                    <>
                                      <SpinnerIcon />
                                      Loading Manual Signing...
                                    </>
                                  ) : (
                                    <>
                                      <div className="ml-1.5 flex items-center">
                                        E Signature
                                      </div>
                                    </>
                                  )}
                                </button>
                                <Menu as="div">
                                  <Menu.Button
                                    ref={trigger}
                                    className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                              bg-sflPrimary hover:bg-green-600 hover:shadow-lg  rounded-l-none rounded-r-md
                                                    `}
                                  >
                                    <span className="sr-only">Open options</span>
                                    <ChevronDownIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </Menu.Button>
                                  <div ref={container} className="z-50">
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items>
                                        <div className="py-1">
                                          <div>
                                            <Menu.Item>
                                              {({ active }) => (
                                                <button
                                                  type="submit"
                                                  className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                                                      hover:bg-green-600 hover:shadow-lg rounded bg-sflPrimary
                                                                `}
                                                  onClick={handleManualSigning}
                                                >
                                                  {isSubmitting ? (
                                                    <>
                                                      <SpinnerIcon />
                                                      Saving...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <div className="ml-1.5 flex items-center">
                                                        Manual Signing
                                                      </div>
                                                    </>
                                                  )}
                                                </button>
                                              )}
                                            </Menu.Item>
                                          </div>
                                          {isLegalDepartmentReviewOrApprove && (
                                            <div className="mt-2">
                                              <Menu.Item>
                                                {({ active }) => (
                                                  <button
                                                    type="button"
                                                    className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                                                      hover:bg-green-600 hover:shadow-lg rounded bg-sflPrimary
                                                                `}
                                                    onClick={
                                                      handleSubmitToRegistrar
                                                    }
                                                  >
                                                    {isSubmitting ? (
                                                      <>
                                                        <SpinnerIcon />
                                                        Saving...
                                                      </>
                                                    ) : (
                                                      <>
                                                        <div className="ml-1.5 flex items-center">
                                                          Submit to Registrar
                                                        </div>
                                                      </>
                                                    )}
                                                  </button>
                                                )}
                                              </Menu.Item>
                                            </div>
                                          )}
                                        </div>
                                      </Menu.Items>
                                    </Transition>
                                  </div>
                                </Menu>
                              </div>
                            )
                          ) : isInManualTransactionReviewContext ? (
                            !agreeToSubmit || !agreeToTerms ? (
                              <button
                                className={`flex items-center px-4 py-2 mb-2 font-semibold mt-7
                           ${!agreeToSubmit || !agreeToTerms
                                    ? "cursor-not-allowed text-white rounded bg-green-focused/20"
                                    : "hover:bg-green-600 hover:shadow-lg text-white rounded bg-sflPrimary"
                                  } // Hover only when enabled
                         `}
                              >
                                Process
                              </button>
                            ) : (
                              <div className={`flex flex-row gap-0.5`}>
                                <button
                                  type="submit"
                                  onClick={handleConfirmDetails}
                                  className={`flex px-2 py-2 button button-hover button-disabled button-outline button-focus
                                     hover:bg-green-600 hover:shadow-lg text-white bg-sflPrimary rounded-md
                                                  `}
                                >
                                  {submitting ? (
                                    <>
                                      <SpinnerIcon />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <div className="ml-1.5 flex items-center">
                                        Process
                                      </div>
                                    </>
                                  )}
                                </button>
                              </div>
                            )
                          ) : (
                            <button
                              data-tip={
                                !isViewMode && (!agreeToSubmit || !agreeToTerms)
                                  ? "Please agree to the terms and conditions to enable this action"
                                  : ""
                              }
                              data-for="button-tooltip"
                              onClick={() => handleConfirmDetails()}
                              disabled={
                                submitting || !agreeToSubmit || !agreeToTerms
                              }
                              className={`flex items-center px-4 py-2 mb-2 font-semibold mt-7
                            ${!agreeToSubmit || !agreeToTerms
                                  ? "cursor-not-allowed text-white rounded bg-green-focused/20"
                                  : "hover:bg-green-600 hover:shadow-lg text-white rounded bg-sflPrimary"
                                } // Hover only when enabled
                          `}
                            >
                              <p>
                                {submitting ? (
                                  <div className="flex">
                                    <SpinnerIcon />
                                    submitting...
                                  </div>
                                ) : (
                                  nextBtnLabel
                                )}
                              </p>
                              {!isViewMode && (
                                <span className="flex items-center ml-4 text-white h-7">
                                  <ChevronRightIcon
                                    className="flex-shrink-0 w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                            </button>
                          )}

                          <ReactTooltip
                            id="button-tooltip"
                            place="bottom"
                            type="dark"
                            effect="solid"
                            delayHide={500}
                            delayShow={100}
                          />
                        </div>
                      ) : (
                        !isLastStep && (
                          <button
                            onClick={goToNextStep}
                            className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
                          >
                            <p className="font-semibold">{nextBtnLabel}</p>
                            <span className="flex items-center ml-4 h-7">
                              <ChevronRightIcon
                                className="flex-shrink-0 w-5 h-5"
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        )
                      )}
                      {isLastStep && isViewMode && (
                        <div>
                          <Button
                            onClick={() =>
                              history.push(
                                `/companies/${params.companyId}/dashboard?transactions`
                              )
                            }
                          >
                            Go To Transactions
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      );
    }
  } else {
    return (
      <div>
        <h1>You do not have review priviledges</h1>
      </div>
    );
  }
};

export default TransactionReviewTab;
